// /: Today for a dealer (what is waiting on me, what got signed, which orders are
// moving) and, for every Phoneware tier, Home: approvals across dealers, orders in
// motion and every dealer this month. Billing ops and Super admins decide what waits;
// Sales and Support see the same queue with Billing ops' name on it (mock M1).
import React, { useState } from "react";
import { formatCents, quoteTotals, roleLabel, type AwaitingBillingApprovalOrder, type CustomerSummary, type Dealer, type Order, type Quote, type QuoteStatus } from "@phoneware/lens-domain";
import { Icon, type IconName } from "../icons.js";
import { Link, navigate, rowLink } from "../router.js";
import { useLoad, useSession, viewerKind } from "../session.js";
import { QUOTE_TYPE, firstName, greeting, plural, sameMonth, when, whenIn } from "../format.js";
import { Callout, Chip, Head, QuoteStatusChip, useTitle } from "../ui.js";
import { Bar, NextMove, orderViewer, stageLabel } from "./Orders.js";
import { ActedCallout } from "./Activity.js";
import { useRepNames } from "./Quotes.js";
import { BillingApprovalSheet, DecisionChip, typeWords, useDecision } from "./BillingApproval.js";
import { useBillingChanges } from "../billing-changes.js";

const WAITING_ORDER: QuoteStatus[] = ["needs_approval", "viewed", "draft"];

function why(q: Quote): string {
  if (q.status === "needs_approval") {
    const r = q.approval.reasons[0];
    return r ? `${r.name} at ${formatCents(r.unitPrice)}, floor ${formatCents(r.floorPrice)}. With Phoneware for approval.` : "With Phoneware for approval.";
  }
  if (q.status === "viewed") return q.link?.viewedAt ? `Opened ${whenIn(q.link.viewedAt)}. Not signed yet.` : "Opened. Not signed yet.";
  if (q.approval.decision === "approved") return "Phoneware approved the price. Ready to send.";
  if (q.approval.decision === "sent_back") return `Phoneware sent it back${q.approval.decisionNote ? `: ${q.approval.decisionNote}` : "."}`;
  return `Draft, last touched ${whenIn(q.updatedAt)}.`;
}

const QUOTE_ICON: Partial<Record<QuoteStatus, IconName>> = { needs_approval: "triangle-alert", viewed: "eye" };

function signedThisMonth(quotes: Quote[]): Quote[] {
  return quotes.filter((q) => q.signature !== null && sameMonth(q.signature.signedAt));
}

function OrderRows({ orders, dealerName }: { orders: Order[]; dealerName: ((id: string) => string) | null }): React.ReactElement {
  const session = useSession();
  const viewer = orderViewer(session);
  if (!orders.length) return <div className="empty">No orders in motion.</div>;
  return (
    <ul className="rows">
      {orders.map((o) => (
        <li key={o.id}>
          <div className="row" {...rowLink(`/orders/${encodeURIComponent(o.id)}`)}>
            <span className="row-ic">
              <Icon name="package" />
            </span>
            <div className="row-main">
              <span className="row-t">{o.customerName}</span>
              <span className="row-m">
                {dealerName ? `${dealerName(o.dealerId)} \u00b7 ` : ""}
                {o.number} · {stageLabel(o)}
              </span>
            </div>
            <div className="row-end">
              <Bar order={o} phoneware={session.everyDealer} />
              <NextMove order={o} viewer={viewer} />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * One order waiting on billing approval: who, how much, and where the account decision stands. Opens the approval sheet
 * for whoever approves billing, and the order for a tier that only watches.
 */
function BillRow({
  order,
  dealerName,
  companyType,
  approves,
  onOpen,
}: {
  order: AwaitingBillingApprovalOrder;
  dealerName: string;
  companyType: number | null;
  approves: boolean;
  onOpen: () => void;
}): React.ReactElement {
  const decision = useDecision(order);
  return (
    <li>
      <div
        className="row to-bill"
        role="button"
        tabIndex={0}
        data-testid={`bill-${order.id}`}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
      >
        <span className="row-ic">
          <Icon name="receipt" />
        </span>
        <div className="row-main">
          <span className="row-t">{order.customerName}</span>
          <span className="row-m">
            {dealerName ? `${dealerName} \u00b7 ` : ""}
            <span className="nw">{order.number}</span>
            {[typeWords(companyType), `waiting since ${whenIn(order.billingApproval?.waitingSince ?? order.updatedAt)}`]
              .filter(Boolean)
              .map((part) => ` \u00b7 ${part}`)
              .join("")}
          </span>
          <span className="row-m row-dec">
            <DecisionChip decision={decision} testId={`bill-decision-${order.id}`} />
            <span className="nw">{decision.text}</span>
          </span>
        </div>
        <div className="row-end">
          <span className="amount">
            {formatCents(order.monthly ?? 0)}
            <small>a month</small>
            <small>{formatCents(order.oneTime ?? 0)} one-time</small>
          </span>
          <span className="chev">
            <Icon name="chevron-right" />
          </span>
        </div>
      </div>
    </li>
  );
}

function SignedTable({ quotes }: { quotes: Quote[] }): React.ReactElement {
  const { me } = useSession();
  if (!quotes.length) return <div className="empty">Nothing signed yet this month.</div>;
  return (
    <div className="tablewrap">
      <table className="t">
        <thead>
          <tr>
            <th>Quote</th>
            <th>Customer</th>
            <th className="num">Monthly</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {quotes.map((q) => (
            <tr key={q.id} {...rowLink(`/quotes/${encodeURIComponent(q.id)}`)}>
              <td className="mono">
                <Link to={`/quotes/${encodeURIComponent(q.id)}`}>{q.number}</Link>
              </td>
              <td>
                <b>{q.customerName}</b>
                <span className="sub">
                  {QUOTE_TYPE[q.type]}, {q.signature ? when(q.signature.signedAt) : ""}
                </span>
              </td>
              <td className="num">{formatCents(quoteTotals(q.lines, me.dealer?.model ?? "agent").monthly)}</td>
              <td>
                <QuoteStatusChip status={q.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DealerToday(): React.ReactElement {
  const { me, api, can, seesPrices } = useSession();
  useTitle("Today");
  const quotes = useLoad<Quote[]>(() => (seesPrices ? api.quotes() : Promise.resolve([])), [seesPrices]);
  const orders = useLoad<Order[]>(() => api.orders(), []);
  const model = me.dealer?.model ?? "agent";
  // Tech sees no prices, so no quotes; New quote is for whoever can write one.
  const canQuote = seesPrices;
  const canStart = can("quotes.write");

  const mine = quotes.data ?? [];
  const waiting = mine
    .filter((q) => WAITING_ORDER.includes(q.status))
    .sort((a, b) => WAITING_ORDER.indexOf(a.status) - WAITING_ORDER.indexOf(b.status) || b.updatedAt.localeCompare(a.updatedAt));
  const moving = (orders.data ?? []).filter((o) => o.status !== "complete").sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const signed = signedThisMonth(mine);
  const signedMonthly = signed.reduce((sum, q) => sum + quoteTotals(q.lines, model).monthly, 0);
  const approvals = mine.filter((q) => q.status === "needs_approval");
  const viewed = mine.filter((q) => q.status === "viewed");
  const held = moving.filter((o) => o.status === "needs_attention");
  const error = quotes.error ?? orders.error;

  const cells: Array<{ to: string; chip: React.ReactNode; text: string }> = [
    model === "wholesale"
      ? { to: "/catalog", chip: <Chip kind="ok" icon="check">Your own prices</Chip>, text: "Nothing waits on approval" }
      : approvals.length
        ? {
          to: `/quotes/${encodeURIComponent(approvals[0].id)}`,
          chip: <Chip kind="warn" icon="triangle-alert">{approvals.length} needs approval</Chip>,
          text: `${approvals[0].customerName}${approvals.length > 1 ? ` and ${approvals.length - 1} more` : ""}`,
        }
        : { to: "/quotes", chip: <Chip kind="ok" icon="check">Floors clear</Chip>, text: "Nothing with Phoneware" },
    viewed.length
      ? { to: `/quotes/${encodeURIComponent(viewed[0].id)}`, chip: <Chip icon="eye">{viewed.length} opened</Chip>, text: `${viewed[0].customerName}, not signed` }
      : { to: "/quotes", chip: <Chip kind="quiet">None opened</Chip>, text: "Nothing sitting unread" },
    { to: "/quotes", chip: <Chip kind="ok" icon="check">{signed.length} signed this month</Chip>, text: `${formatCents(signedMonthly)} a month` },
    held.length
      ? { to: `/orders/${encodeURIComponent(held[0].id)}`, chip: <Chip kind="warn" icon="triangle-alert">{held.length} held up</Chip>, text: "With Phoneware" }
      : moving.length
        ? { to: `/orders/${encodeURIComponent(moving[0].id)}`, chip: <Chip icon="clock">{moving.length} in motion</Chip>, text: moving[0].customerName }
        : { to: "/orders", chip: <Chip kind="ok" icon="check">Orders settled</Chip>, text: "Nothing on you" },
  ];

  return (
    <>
      <Head
        title={`${greeting(new Date())}, ${firstName(me.user.name)}`}
        sub={`${plural(waiting.length, "quote")} waiting on you and ${plural(moving.length, "order")} in motion.`}
        actions={
          canStart ? (
            <button className="btn primary" type="button" onClick={() => navigate("/quotes/new")}>
              <Icon name="plus" size={15} />
              New quote
            </button>
          ) : null
        }
      />
      {error ? (
        <Callout kind="stop" icon="triangle-alert">
          {error.message}
        </Callout>
      ) : null}
      <ActedCallout />
      {canQuote ? (
        <div className="strip">
          {cells.map((c, i) => (
            <Link key={i} to={c.to}>
              {c.chip}
              <span>{c.text}</span>
            </Link>
          ))}
        </div>
      ) : null}
      <div className="grid2">
        {canQuote ? (
          <section className="card">
            <div className="card-h">
              <h2>Waiting on you</h2>
              <Link className="more" to="/quotes">
                All quotes
              </Link>
            </div>
            {!quotes.data ? (
              <div className="empty">Loading quotes.</div>
            ) : waiting.length === 0 ? (
              <div className="empty">{mine.length ? "Nothing waiting. Everything in front of you is signed or sent." : "No quotes yet. Start one with New quote."}</div>
            ) : (
              <ul className="rows">
                {waiting.map((q) => (
                  <li key={q.id}>
                    <div className="row" {...rowLink(`/quotes/${encodeURIComponent(q.id)}`)}>
                      <span className="row-ic">
                        <Icon name={QUOTE_ICON[q.status] ?? "file-text"} />
                      </span>
                      <div className="row-main">
                        <span className="row-t">
                          {q.customerName} <span className="lite muted">· {q.number}</span>
                        </span>
                        <span className="row-m">{why(q)}</span>
                      </div>
                      <div className="row-end">
                        <QuoteStatusChip status={q.status} />
                        <span className="amount w88">
                          {formatCents(quoteTotals(q.lines, model).monthly)}
                          <small>a month</small>
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
        <section className="card">
          <div className="card-h">
            <h2>Orders in motion</h2>
            <Link className="more" to="/orders">
              All orders
            </Link>
          </div>
          {orders.data ? <OrderRows orders={moving} dealerName={null} /> : <div className="empty">Loading orders.</div>}
          {moving.some((o) => o.status === "awaiting_billing_approval") ? (
            <p className="card-note" data-testid="today-waiting-note">
              Waiting on Phoneware means Phoneware is checking billing before OneBill bills your customer. Nothing is needed from you.
            </p>
          ) : null}
        </section>
      </div>
      {canQuote ? (
        <section className="card">
          <div className="card-h">
            <h2>Signed this month</h2>
            <Link className="more" to="/quotes">
              Quotes
            </Link>
          </div>
          {quotes.data ? <SignedTable quotes={signed} /> : <div className="empty">Loading quotes.</div>}
        </section>
      ) : null}
    </>
  );
}

function OpsHome(): React.ReactElement {
  const { me, api, can } = useSession();
  useTitle("Home");
  // Billing ops and Super admins decide what waits; the other tiers see the same queue with Billing ops' name on it.
  const approves = can("billing.approve");
  const decides = can("approvals.decide");
  const changes = useBillingChanges();
  const approvals = useLoad<Quote[]>(() => api.approvals(), []);
  const toBill = useLoad<AwaitingBillingApprovalOrder[]>(() => api.ordersToBill(), [changes]);
  const orders = useLoad<Order[]>(() => api.orders(), [changes]);
  const [approving, setApproving] = useState<string | null>(null);
  const dealers = useLoad<Dealer[]>(() => api.dealers(), []);
  const quotes = useLoad<Quote[]>(() => api.quotes(), []);
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  const repName = useRepNames();
  const error = approvals.error ?? toBill.error ?? orders.error ?? dealers.error ?? quotes.error ?? customers.error;

  const dealerName = (id: string) => dealers.data?.find((d) => d.id === id)?.name ?? "";
  const companyTypeOf = (id: number) => customers.data?.find((c) => c.autotaskCompanyId === id)?.companyType ?? null;
  const waiting = approvals.data ?? [];
  // Oldest first: the order that has waited longest is the next to approve.
  const bill = [...(toBill.data ?? [])].sort((a, b) => (a.billingApproval?.waitingSince ?? a.updatedAt).localeCompare(b.billingApproval?.waitingSince ?? b.updatedAt));
  const billSum = bill.reduce((t, o) => ({ monthly: t.monthly + (o.monthly ?? 0), oneTime: t.oneTime + (o.oneTime ?? 0) }), { monthly: 0, oneTime: 0 });
  // Orders to bill sit above; the rest of what is still moving is here.
  const moving = (orders.data ?? [])
    .filter((o) => o.status !== "complete" && o.status !== "awaiting_billing_approval")
    .sort((a, b) => Number(b.status === "needs_attention") - Number(a.status === "needs_attention") || b.updatedAt.localeCompare(a.updatedAt));

  return (
    <>
      <Head
        title="Phoneware operations"
        sub={
          <>
            Every dealer, every quote, one pane. Your tier is <b data-testid="home-tier">{roleLabel(me.user.role)}</b>.
          </>
        }
        actions={
          <>
            <Link className="btn" to="/dealers">
              Dealers
            </Link>
            {decides || approves ? (
              <Link className="btn primary" to="/approvals">
                Open approvals
              </Link>
            ) : (
              <>
                <Link className="btn" to="/approvals">
                  Approvals
                </Link>
                {can("quotes.write") ? (
                  <Link className="btn primary" to="/quotes/new" data-testid="home-new-quote">
                    <Icon name="plus" size={15} />
                    New quote
                  </Link>
                ) : can("mac.start") ? (
                  <Link className="btn primary" to="/orders/mac/new" data-testid="home-new-mac">
                    <Icon name="plus" size={15} />
                    New MAC order
                  </Link>
                ) : null}
              </>
            )}
          </>
        }
      />
      {error ? (
        <Callout kind="stop" icon="triangle-alert">
          {error.message}
        </Callout>
      ) : null}
      <section className="card" data-testid="ops-waiting">
        <div className="card-h wrap">
          <h2>Waiting on Phoneware</h2>
          {bill.length ? (
            <Chip kind={approves ? "warn" : "quiet"} icon="receipt" testId="ops-bill-count">
              {plural(bill.length, "order")} to bill
            </Chip>
          ) : null}
          {waiting.length ? (
            <Chip kind={decides ? "warn" : "quiet"} icon="triangle-alert">
              {waiting.length} below the floor
            </Chip>
          ) : null}
          {approves || decides ? null : (
            <Chip kind="quiet" icon="clock" testId="ops-on-billing">
              On Billing ops
            </Chip>
          )}
          <Link className="more" to="/approvals">
            Approvals
          </Link>
        </div>
        <div className="sub-h">
          <h3>Orders to bill</h3>
          {bill.length ? (
            <span className="note" data-testid="ops-bill-sum">
              {formatCents(billSum.monthly)} a month and {formatCents(billSum.oneTime)} one-time wait on {approves ? "your approval" : "Billing ops"}. OneBill bills none of it until then.
            </span>
          ) : null}
        </div>
        {!toBill.data ? (
          <div className="empty">Loading orders to bill.</div>
        ) : bill.length === 0 ? (
          <div className="empty">No orders waiting to bill. Every signed order has its billing approved.</div>
        ) : (
          <ul className="rows" data-testid="ops-bill">
            {bill.map((o) => (
              <BillRow
                key={o.id}
                order={o}
                dealerName={dealerName(o.dealerId)}
                companyType={companyTypeOf(o.customerId)}
                approves={approves}
                onOpen={() => (approves ? setApproving(o.id) : navigate(`/orders/${encodeURIComponent(o.id)}`))}
              />
            ))}
          </ul>
        )}
        <div className="sub-h">
          <h3>Quotes below the floor</h3>
        </div>
        {!approvals.data ? (
          <div className="empty">Loading approvals.</div>
        ) : waiting.length === 0 ? (
          <div className="empty">No quotes below the floor.</div>
        ) : (
          <ul className="rows">
            {waiting.map((q) => {
              const r = q.approval.reasons[0];
              const under = q.approval.reasons.reduce((sum, x) => {
                const line = q.lines.find((l) => l.productId === x.productId);
                return line && line.billing === "monthly" ? sum + (x.floorPrice - x.unitPrice) * line.qty : sum;
              }, 0);
              return (
                <li key={q.id}>
                  <div className="row" {...rowLink(`/quotes/${encodeURIComponent(q.id)}`)}>
                    <span className="row-ic">
                      <Icon name="triangle-alert" />
                    </span>
                    <div className="row-main">
                      <span className="row-t">
                        {q.customerName}{" "}
                        <span className="lite muted">
                          · {dealerName(q.dealerId)} · {q.number}
                        </span>
                      </span>
                      <span className="row-m">
                        {r ? `${r.name} at ${formatCents(r.unitPrice)}, floor ${formatCents(r.floorPrice)}. ` : ""}
                        {repName(q.repEmail)}, asked {whenIn(q.updatedAt)}
                      </span>
                    </div>
                    <div className="row-end">
                      <span className="amount">
                        {formatCents(-under)}
                        <small>a month under</small>
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <section className="card">
        <div className="card-h">
          <h2>Orders in motion</h2>
          <Link className="more" to="/orders">
            All orders
          </Link>
        </div>
        {orders.data ? <OrderRows orders={moving} dealerName={dealerName} /> : <div className="empty">Loading orders.</div>}
      </section>
      {approving ? (
        <BillingApprovalSheet
          orderId={approving}
          onClose={() => {
            setApproving(null);
            toBill.reload();
            orders.reload();
          }}
        />
      ) : null}
      <section className="card">
        <div className="card-h">
          <h2>Dealers this month</h2>
          <span className="note">Quoted and signed this month</span>
        </div>
        {!dealers.data ? (
          <div className="empty">Loading dealers.</div>
        ) : dealers.data.length === 0 ? (
          <div className="empty">
            No dealers yet. <Link to="/dealers">Add the first one on Dealers.</Link>
          </div>
        ) : (
          <div className="tablewrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Dealer</th>
                  <th className="hide-sm">Model</th>
                  <th className="num hide-sm">Customers</th>
                  <th className="num">Quoted</th>
                  <th className="num">Signed</th>
                  <th>Waiting on us</th>
                </tr>
              </thead>
              <tbody>
                {dealers.data.map((d) => {
                  const theirs = (quotes.data ?? []).filter((q) => q.dealerId === d.id);
                  const asks = waiting.filter((q) => q.dealerId === d.id).length;
                  const toBillHere = bill.filter((o) => o.dealerId === d.id).length;
                  return (
                    <tr key={d.id} {...rowLink("/dealers")}>
                      <td>
                        <b>{d.name}</b>
                      </td>
                      <td className="hide-sm">{d.model === "agent" ? "Dealer" : "Reseller"}</td>
                      <td className="num hide-sm">{(customers.data ?? []).filter((c) => c.dealerId === d.id).length}</td>
                      <td className="num">{theirs.filter((q) => sameMonth(q.createdAt)).length}</td>
                      <td className="num">{signedThisMonth(theirs).length}</td>
                      <td>
                        {asks ? (
                          <Chip kind="warn" icon="triangle-alert">
                            {plural(asks, "approval")}
                          </Chip>
                        ) : null}
                        {asks && toBillHere ? " " : null}
                        {toBillHere ? (
                          <Chip kind="warn" icon="receipt">
                            {toBillHere} to bill
                          </Chip>
                        ) : null}
                        {asks || toBillHere ? null : <span className="muted">None</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="card-note">A dealer sells at Phoneware's prices and is paid commission. A reseller buys at a flat cost and marks up; Phoneware still bills both.</p>
      </section>
    </>
  );
}

export function Today(): React.ReactElement {
  const { me } = useSession();
  const kind = viewerKind(me.user);
  switch (kind) {
    case "admin":
      return <OpsHome />;
    case "dealer":
      return <DealerToday />;
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
}
